import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { Microscope, Loader2 } from "lucide-react";

export default function CrossCurricular() {
  const [modes, setModes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [question, setQuestion] = useState("");
  const [source, setSource] = useState("");
  const [draft, setDraft] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cross-curricular/modes").then(r => {
      setModes(r.data.modes || []);
      setSelected(r.data.modes?.[0]?.id);
    });
  }, []);

  const submit = async () => {
    if (!selected) return;
    if (!question.trim()) { toast.error("Add the question / prompt first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/cross-curricular/coach", {
        mode_id: selected, question, source_material: source, student_draft: draft,
      });
      setResult(data);
      celebrateBadges(data._meta);
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail.message);
      else toast.error("Could not fetch coaching.");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center"><Microscope size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Cross-Curricular Literacy</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">Writing in science class isn't the same as writing in English class. Coaching for CER, DBQ, science labs, and analytical reading across disciplines.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-5">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2 block">Pick a writing task</label>
          <div className="flex flex-wrap gap-2">
            {modes.map(m => (
              <button key={m.id} data-testid={`cc-mode-${m.id}`} type="button"
                onClick={()=>setSelected(m.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold border-2 ${selected===m.id ? 'bg-sky-500 border-sky-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-sky-300'}`}>
                {m.name} <span className="opacity-70">· {m.subject}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Question / prompt</label>
          <textarea data-testid="cc-question" value={question} onChange={(e)=>setQuestion(e.target.value)} rows={2}
            placeholder='e.g. "Using the documents provided, explain how the printing press transformed European society."'
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 px-4 py-3 outline-none resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Source material / data (optional)</label>
          <textarea data-testid="cc-source" value={source} onChange={(e)=>setSource(e.target.value)} rows={4}
            placeholder="Paste the passage, lab data, or document excerpts."
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 px-4 py-3 outline-none resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your draft (optional)</label>
          <textarea data-testid="cc-draft" value={draft} onChange={(e)=>setDraft(e.target.value)} rows={5}
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 px-4 py-3 outline-none resize-none" />
        </div>
        <button data-testid="cc-submit" onClick={submit} disabled={loading}
          className="bg-sky-500 hover:bg-sky-400 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
          style={{boxShadow:'0 4px 0 0 rgb(2 132 199)'}}>
          {loading ? <><Loader2 className="animate-spin" size={16}/> Coaching…</> : "Coach me"}
        </button>
      </div>

      {result && (
        <div data-testid="cc-result" className="space-y-4 fade-up">
          <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
            <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-1">What the question is really asking</div>
            <p className="text-slate-900">{result.task_breakdown}</p>
          </div>
          <div className="tactile-card p-6">
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-3">Required moves for this task</div>
            <div className="space-y-3">
              {(result.required_moves || []).map((m, i) => (
                <div key={i} className="rounded-2xl border-2 border-slate-100 p-4">
                  <div className="font-bold text-slate-900 mb-1" style={{fontFamily:'Outfit'}}>{m.name}</div>
                  <div className="text-sm text-slate-700"><strong className="text-emerald-700">How:</strong> {m.how}</div>
                  {m.watch_out && <div className="text-sm text-slate-700 mt-1"><strong className="text-rose-700">Watch out:</strong> {m.watch_out}</div>}
                </div>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="tactile-card p-6 bg-emerald-50 border-emerald-100">
              <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-1">Evidence strategy</div>
              <p className="text-slate-800">{result.evidence_strategy}</p>
            </div>
            <div className="tactile-card p-6 bg-amber-50 border-amber-100">
              <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-1">Discipline voice</div>
              <p className="text-slate-800">{result.discipline_voice}</p>
            </div>
          </div>
          {result.sample_opening && (
            <div className="tactile-card p-6 bg-violet-50 border-violet-100">
              <div className="text-xs uppercase font-bold tracking-widest text-violet-800 mb-1">Sample opening sentence</div>
              <p className="text-slate-900 italic">"{result.sample_opening}"</p>
            </div>
          )}
          {result.draft_feedback && (
            <div className="tactile-card p-6 bg-rose-50 border-rose-100">
              <div className="text-xs uppercase font-bold tracking-widest text-rose-800 mb-1">Coach feedback on your draft</div>
              <p className="text-slate-800">{result.draft_feedback}</p>
            </div>
          )}
          {result.next_step && (
            <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
              <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-1">Next step</div>
              <p className="text-slate-900 font-semibold">{result.next_step}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
